// src/components/Memory/Cards.tsx
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCardFlip from 'react-card-flip';
import Tilt from 'react-parallax-tilt';
import classes from './Cards.module.less';
import { ReactComponent as Leaf } from '../../assets/icons/games/memory/leaf-outline.svg';

type CardProps = {
  id: string;
  text?: string;
  image?: string;
  type?: 'word' | 'image';
  isFinished?: boolean;
  isFlipped?: boolean;
  isDisabled?: boolean;
  handleClick?: (id: string) => void;
  handleDoubleClick?: (id: string) => void;
};

export const Card: FC<CardProps> = React.memo(({
  handleClick,
  handleDoubleClick,
  text,
  image,
  type,
  id,
  isFlipped = false,
  isFinished = false,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const onCardClick = useCallback(() => {
    if (!isDisabled && !isFinished && !isFlipped && handleClick) {
      handleClick(id);
    }
  }, [id, isDisabled, isFinished, isFlipped, handleClick]);

  const onCardDoubleClick = useCallback(() => {
    if (handleDoubleClick) {
      handleDoubleClick(id);
    }
  }, [id, handleDoubleClick]);

  return (
    <Tilt
      key={isFinished ? `finished-${id}` : `active-${id}`}
      tiltEnable={!isFinished && !isDisabled}
      tiltMaxAngleX={15}
      tiltMaxAngleY={15}
      scale={1.05}
    >
      <ReactCardFlip
        containerClassName={isFinished ? classes.cardFinished : ''}
        isFlipped={isFlipped || isFinished}
        flipDirection="vertical"
        flipSpeedFrontToBack={0.2}
        flipSpeedBackToFront={0.2}
      >
        <div
          className={`${classes.card} ${isDisabled ? classes.cardDisabled : ''}`}
          onClick={onCardClick}
          aria-hidden="true"
          id={id}
        >
          <Leaf className="leaf" />
        </div>

        <div
          className={classes.cardTranslation}
          id={id}
          onDoubleClick={onCardDoubleClick}
        >
          {type === 'image' && image && (
            <img
              src={image}
              alt={text || t('games:labels.cardImage')}
              className={classes.cardImage}
            />
          )}
          {type === 'word' && <span className={classes.cardWord}>{text}</span>}
        </div>
      </ReactCardFlip>
    </Tilt>
  );
}, (prevProps, nextProps) => (prevProps.isFlipped === nextProps.isFlipped
    && prevProps.isFinished === nextProps.isFinished
    && prevProps.isDisabled === nextProps.isDisabled
    && prevProps.id === nextProps.id
));

Card.displayName = 'Card';

export default Card;
